<template>
    <div ref="root" tabindex="-1" role="region" :aria-label="ariaLabel">
        <!-- Info -->
        <v-snackbar top :color="info.color" v-model="showInfo">
            <v-icon>{{ info.icon }}</v-icon>
            <span class="font-weight-bold pl-2">{{ info.message }}</span>
        </v-snackbar>
        <v-data-table
            id="simple-projects-table"
            class="row-pointer"
            hide-default-footer
            :footer-props="{ 'items-per-page-options': [50, 100, 250] }"
            :items-per-page="-1"
            :headers="researchHeaders"
            :items="researches"
            :sort-by.sync="project_sort_by"
            :sort-desc.sync="project_sort_desc">
            <template v-for="(h, i) in researchHeaders" v-slot:[`header.${h.value}`]="{ header }">
                <span :key="i">
                    <span
                        :style="{ width: header.width }"
                        :aria-sort="project_sort_by && project_sort_by[0] === header.value ? (project_sort_desc && project_sort_desc[0] ? 'descending' : 'ascending') : 'none'">
                        <template v-if="header.sortable !== false">
                            <v-tooltip v-if="header.tooltip" color="primary" top max-width="250px" open-on-focus>
                                <template #activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" small text class="th-btn th-btn--header mr-1" :aria-describedby="`tt-${header.value}`">
                                        <span class="th-text">{{ header.text }}</span>
                                        <v-icon x-small aria-hidden="true" class="ml-1">mdi-help</v-icon>
                                    </v-btn>
                                </template>
                                <span :id="`tt-${header.value}`" role="tooltip">{{ header.tooltip }}</span>
                            </v-tooltip>

                            <v-btn v-else small text class="th-btn th-btn--header">
                                <span class="th-text">{{ header.text }}</span>
                            </v-btn>

                            <!-- ARIA Sort-Status -->
                            <span class="sr-only" aria-live="polite">
                                <span v-if="project_sort_by && project_sort_by[0] === header.value && !(project_sort_desc && project_sort_desc[0])"> (sorted in ascending order)</span>
                                <span v-else-if="project_sort_by && project_sort_by[0] === header.value && project_sort_desc && project_sort_desc[0]"> (sorted in descending order)</span>
                            </span>
                        </template>

                        <!-- Nicht-sortierbar: nur Text -->
                        <template v-else>
                            <span class="th-text">{{ header.text }}</span>
                        </template>
                    </span>
                </span>
            </template>
            <template v-slot:body="{ items }">
                <tbody>
                    <tr
                        v-for="item in items"
                        :key="item.listname"
                        @click="onColumnClick(item, $event)"
                        tabindex="0"
                        role="link"
                        :aria-label="`Open collection ${item.listname}`"
                        @keydown.enter.prevent="onColumnClick(item, $event)"
                        @keydown.space.prevent="onColumnClick(item, $event)">
                        <td>
                            <div>
                                <a
                                    v-if="item.status != 'creating' && item.status != 'updating'"
                                    :href="getResearchHref(item)"
                                    class="listnameColumn simple-listname-link"
                                    :aria-label="`Open collection ${item.listname}`"
                                    @click.stop.prevent="onColumnClick(item, $event)">
                                    {{ item.listname }}
                                </a>
                                <span v-else>{{ item.listname }}</span>
                                <v-tooltip v-if="item.status === collectionErrorStatus" top max-width="300px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" small color="warning" class="ml-1" role="img" :aria-label="collectionErrorHint">mdi-alert-circle-outline</v-icon>
                                    </template>
                                    <span>{{ collectionErrorHint }}</span>
                                </v-tooltip>
                            </div>
                        </td>

                        <td>
                            <div v-if="item.status != 'creating' && item.status != 'updating'">
                                <v-chip small outlined color="primary" :style="'background-color: rgb(237 239 241 /' + ($vuetify.theme.light == false ? '35%)' : '10%)') + '!important'">
                                    <v-icon class="mr-1" color="grey" size="12px">mdi-key</v-icon>
                                    <span class="researchKwCount"> {{ $helpers.formatNumber(item.count) }}</span>
                                </v-chip>
                            </div>
                            <div v-else>
                                <v-progress-circular indeterminate color="primary" aria-label="Loading"></v-progress-circular>
                            </div>
                        </td>

                        <td>
                            <div v-if="item.status !== 'creating' && item.status != 'updating'">
                                <v-row no-gutters>
                                    <v-col class="d-flex">
                                        <v-sparkline
                                            class="my-auto"
                                            style="max-width: 110px; max-height: 40px"
                                            id="sparkline"
                                            color="primary"
                                            :value="item.trendData.data"
                                            padding="2"
                                            smooth="20"
                                            gradient-direction="top"
                                            auto-draw
                                            :fill="false"></v-sparkline>
                                        <v-sparkline
                                            class="my-auto"
                                            style="max-width: 110px; max-height: 40px; position: absolute"
                                            id="sparkline"
                                            color="primary"
                                            :value="item.trendData.data"
                                            :gradient="gradient"
                                            padding="2"
                                            smooth="20"
                                            gradient-direction="top"
                                            auto-draw
                                            :fill="true"></v-sparkline>
                                        <v-row outlined small class="qchip my-auto pl-3" v-if="item.trendData.change">
                                            <span :style="'color: ' + item.trendData.icon_color" class="my-auto ml-2">{{ Math.abs(item.trendData.change) + "%" }} </span>
                                            <v-icon size="16px" :color="item.trendData.icon_color">{{ item.trendData.icon }}</v-icon>
                                        </v-row>
                                        <v-row outlined small class="qchip my-auto pl-3" v-else>
                                            <span :style="'color: grey'" class="my-auto ml-2">{{ Math.abs(item.trendData.change) + "%" }} </span>
                                        </v-row>
                                        <span class="sr-only">Trend change {{ item.trendData.change >= 0 ? "up" : "down" }} by {{ Math.abs(item.trendData.change) }} percent.</span>
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-else>
                                <v-progress-circular indeterminate color="primary" aria-label="Loading"></v-progress-circular>
                            </div>
                        </td>

                        <td>
                            <div v-if="item.status != 'creating' && item.status != 'updating'">
                                <v-row>
                                    <v-col class="ma-auto" style="display: flex">
                                        <v-icon class="my-auto mr-1" small>mdi-map-marker</v-icon>
                                        {{ item.country }}
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-else>
                                <v-progress-circular indeterminate color="primary" aria-label="Loading"></v-progress-circular>
                            </div>
                        </td>

                        <td>
                            <div v-if="item.status != 'creating' && item.status != 'updating'" class="d-flex">
                                <v-icon class="my-auto mr-1" small>mdi-clock-time-eight</v-icon>
                                <span v-if="item.created">{{ $helpers.returnResearchDate(item.created) }}</span>
                            </div>
                            <div v-else>
                                <v-progress-circular indeterminate color="primary" aria-label="Loading"></v-progress-circular>
                            </div>
                        </td>

                        <td>
                            <v-menu rounded="lg" color="primary" transition="slide-y-transition" bottom v-if="item.status != 'creating' && item.status != 'updating'">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        v-if="
                                            $helpers.isActionAllowed(2, user_rights) ||
                                            $helpers.isActionAllowed(3, user_rights) ||
                                            $helpers.isActionAllowed(4, user_rights) ||
                                            (decryptedId == item.owner && $helpers.isActionAllowed(28, user_rights)) ||
                                            $helpers.isActionAllowed(34, user_rights)
                                        "
                                        id="cogbtn"
                                        icon
                                        v-on="on"
                                        v-bind="attrs"
                                        aria-haspopup="menu"
                                        :aria-label="`More actions for ${item.listname}`">
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>
                                <v-list rounded dense role="menu">
                                    <v-list-item
                                        v-if="item.status != 'creating' && item.status != 'updating' && $helpers.isActionAllowed(4, user_rights)"
                                        @click="$emit('openShareMenu', item)"
                                        role="menuitem">
                                        <v-icon small class="mr-2">mdi-share-variant</v-icon><v-list-item-title>Share</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item
                                        v-if="item.status != 'creating' && item.status != 'updating' && $helpers.isActionAllowed(2, user_rights)"
                                        @click="$emit('initChangeNameDialog', item)"
                                        role="menuitem">
                                        <v-icon small class="mr-2">mdi-square-edit-outline</v-icon><v-list-item-title>Rename</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item
                                        v-if="
                                            item.status != 'creating' &&
                                            item.status != 'updating' &&
                                            ($helpers.isActionAllowed(3, user_rights) || (decryptedId == item.owner && $helpers.isActionAllowed(28, user_rights)))
                                        "
                                        @click="deleteListFromProject(item._id, item.project)"
                                        role="menuitem">
                                        <v-icon small class="mr-2">mdi-close</v-icon><v-list-item-title>Remove</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-data-table>
    </div>
</template>

<script>
    import { COLLECTION_ERROR_STATUS, COLLECTION_ERROR_HINT } from "../../utils/collectionBusy"

    export default {
        name: "SimpleResearchTable",
        props: {
            researches: Array,
            autofocus: { type: Boolean, default: false },
            ariaLabel: { type: String, default: "Project collections" }
        },
        data() {
            return {
                collectionErrorStatus: COLLECTION_ERROR_STATUS,
                collectionErrorHint: COLLECTION_ERROR_HINT,
                gradient:[this.$vuetify.theme.themes.light.sparklineGradient1, this.$vuetify.theme.themes.light.sparklineGradient2, this.$vuetify.theme.themes.light.sparklineGradient3],

                researchHeaders: [
                    {
                        sortable: true,
                        text: "RESEARCH",
                        value: "listname",
                        tooltip: "The name of the research collection"
                    },
                    {
                        sortable: true,
                        text: "KEYWORDS",
                        value: "count",
                        tooltip: "The number of keywords in the collection"
                    },
                    {
                        sortable: true,
                        text: "TREND (GOOGLE SV)",
                        value: "trendData.change",
                        align: "center",
                        tooltip: "The trend change in Google Search"
                    },
                    {
                        sortable: true,
                        text: "COUNTRY",
                        value: "country",
                        tooltip: "The country of the collection"
                    },
                    {
                        sortable: true,
                        text: "CREATED",
                        value: "created",
                        tooltip: "The creation date of the collection"
                    },

                    {
                        sortable: false,
                        text: " ",
                        value: "action"
                    }
                ],
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH),
                info: {
                    message: null,
                    icon: null,
                    color: null
                },
                showInfo: false,
                project_sort_desc: [true],
                project_sort_by: ["count"]
            }
        },
        watch: {
            project_sort_desc() {
                console.log(this.project_sort_desc)
                this.user_settings.collections_projects_project_sort.sort = this.project_sort_desc
                this.user_settings.collections_projects_project_sort.column = this.project_sort_by

                this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            project_sort_by() {
                console.log(this.project_sort_by)
                // this.$store.dispatch("CHANGE_USER_SETTINGS", this.user_settings)
            },
            autofocus(val) {
                if (val) this.$nextTick(this.focusRoot)
            }
        },
        methods: {
            focusRoot() {
                const el = this.$refs.root
                if (el && typeof el.focus === "function") el.focus()
            },
            getResearchHref(item) {
                return this.$router.resolve({ path: "/research/" + item.listname }).href
            },
            onColumnClick(item, evt) {
                if (evt && typeof evt.stopPropagation === "function") evt.stopPropagation()
                if (item.status == "creating") {
                    this.createInfo("This collection is currently being created", "#fcba03", "mdi-alert-circle-outline")
                } else if (item.status == "updating") {
                    this.createInfo("This collection is currently being updated", "#fcba03", "mdi-alert-circle-outline")
                } else {
                    this.$router.push({ path: "/research/" + item.listname })
                }
            },
            deleteListFromProject(list_id, project) {
                this.$http
                    .post(
                        process.env.VUE_APP_APIURL + "/projects/delete/list",
                        {
                            list_id: list_id,
                            project: project,
                            company_id: localStorage.company_id
                        },
                        { headers: { Authorization: this.auth } }
                    )
                    .then(function (response) {
                        // console.log(response)
                        this.$emit("removeCollection", list_id, project)
                    })
            },
            createInfo(message, color, icon) {
                console.log("createinfo")
                this.info.message = message
                this.info.color = color
                this.info.icon = icon
                this.showInfo = true
            }
        },
        computed: {
            user_rights() {
                return this.$store.state.user_rights
            },
            user_settings() {
                return this.$store.state.user_settings
            }
        },
        mounted() {
            if (this.autofocus) this.$nextTick(this.focusRoot)
            console.log(this.user_settings)
            this.project_sort_desc = this.user_settings.collections_projects_project_sort.sort
            this.project_sort_by = this.user_settings.collections_projects_project_sort.column
        }
    }
</script>

<style>
    #simple-projects-table th {
        background-color: rgb(224, 224, 224);
        font-weight: 100;
    }
    #simple-projects-table tr {
        cursor: pointer !important;
    }
    .updated-subtitle {
        font-size: 0.8em;
    }
    .bold {
        font-weight: bold;
    }
    .welcommessage {
        font-family: Nunito;
        font-weight: 100;
        padding-bottom: 0px;
    }
    #welcomecard {
        padding: 15px;
        color: white;
        background: linear-gradient(56deg, rgba(28, 237, 178, 1) 0%, rgba(0, 159, 138, 1) 0%, rgb(28, 237, 178) 100%);
    }
    .welcometitle {
        font-family: Nunito;
        font-weight: 100;
        text-align: center;
    }
    .subtitle {
        text-align: center;
        font-family: Nunito;
    }
    .chiptext {
        font-weight: bold;
        color: var(--v-primary-base);
    }
    .sitoquaro div {
        display: inline;
        padding: 10px;
    }
    .sitoquaro {
        text-align: center;
        margin-left: auto;
    }
    #tablesheet {
        margin-left: 5px;
        margin-top: -30px;
        width: 60px;
        height: 60px;
    }
    #heading3 {
        font-weight: 300 !important;
        color: rgb(105, 105, 105);
    }
    .headingsheet2 {
        margin-top: -15px;
    }
    #sheeticon {
        margin-left: -25px;
        margin-top: -25px;
        margin: auto !important;
        padding-bottom: 50% !important;
        width: 50% !important;
    }
    .project-title {
        font-weight: bolder;
        font-size: 1.1em;
    }
    .avat {
        background: linear-gradient(56deg, var(--v-left_nav_gradient1-base) 0%, var(--v-left_nav_gradient3-base) 100%);
        margin-top: 1px;
    }
    .simple-listname-link,
    .simple-listname-link:hover,
    .simple-listname-link:focus,
    .simple-listname-link:visited,
    .simple-listname-link:active {
        color: inherit !important;
        text-decoration: none !important;
    }
    td[role="link"]:focus-visible,
    #cogbtn:focus-visible {
        outline: 3px solid var(--v-primary-base);
        outline-offset: 2px;
        border-radius: 4px;
    }

    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0 0 0 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
    #simple-projects-table tbody tr[role="link"]:focus-visible,
    #simple-projects-table tbody tr[role="link"]:focus,
    #simple-projects-table tbody tr[role="link"]:focus-within {
        outline: 3px solid var(--v-primary-base) !important;
        outline-offset: 2px;
        border-radius: 4px;
    }
</style>
